import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { formatCurrency } from "../format";
import type { Row } from "../types";
import { IconBriefcase, IconClose, IconSearch } from "../icons";
import { ordenarPorNome } from "../ordem";

/**
 * Campo CARGO por SELEÇÃO: em vez de digitar, quem cadastra abre um popup e
 * MARCA o cargo do colaborador na tabela de Cargos e Salários do RH.
 *
 * Motivo: cargo em texto livre gerava grafias diferentes para o mesmo cargo
 * ("assistente rh", "Assistente de RH"...), o que atrapalha os filtros, o
 * espelhamento para os outros sistemas e qualquer comparação com a tabela
 * salarial. Agora a fonte é sempre a aba "Cargos e Salários".
 *
 * Grava no MESMO campo de texto de antes. No Plano de Cargos e Salários 2026
 * cada cargo já é um nível ("Montador de Equipamentos Elétricos Júnior"), então
 * grava só o NOME do cargo; a tabela anterior (arquivada) gravava "Cargo -
 * Nível". Cargo antigo, cadastrado antes desta tela, NÃO se perde: aparece no
 * topo do popup como "manter o cargo atual".
 *
 * Além dos cargos do plano, o popup oferece os cargos das TRILHAS DE
 * CRESCIMENTO que ainda não têm ocupante (ex.: "Eletricista Júnior"), com o
 * salário sugerido da trilha — é o destino natural de uma promoção.
 */

interface CargoOpcao {
  cargo: string;
  departamento: string | null;
  nivel: string | null;
  salario: number | null;
  /** Texto gravado no cadastro do colaborador. */
  texto: string;
  /** Veio de uma trilha de crescimento: o valor é o salário SUGERIDO. */
  sugerido?: boolean;
}

interface Grupo {
  cargo: string;
  departamento: string | null;
  /** Bloco onde o cargo aparece: o eixo do plano (ou o departamento). */
  secao: string;
  /** Cargo que só existe nas trilhas — vai para os blocos do fim. */
  daTrilha?: boolean;
  opcoes: CargoOpcao[];
}

/** Bloco de um eixo/departamento com todos os cargos dele, um embaixo do outro. */
interface Secao {
  departamento: string;
  daTrilha: boolean;
  grupos: Grupo[];
}

/** Cargo sem departamento na tabela cai neste bloco, no fim da lista. */
const SEM_DEPARTAMENTO = "Outros cargos";

/** Compara ignorando acento, caixa e espaços repetidos. */
function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Agrupa os registros por cargo, mantendo a ordem que veio do servidor. */
export function agrupar(rows: Row[]): Grupo[] {
  const mapa = new Map<string, Grupo>();
  for (const r of rows) {
    const cargo = String(r.cargo ?? "").trim();
    if (!cargo) continue;
    const nivel = r.nivel ? String(r.nivel).trim() : null;
    const departamento = r.departamento ? String(r.departamento).trim() : null;
    const opcao: CargoOpcao = {
      cargo,
      departamento,
      nivel,
      salario: r.salario == null || r.salario === "" ? null : Number(r.salario),
      // Plano 2026: um registro por cargo, o nível já está no nome.
      texto: r.plano === "2026" || !nivel ? cargo : `${cargo} - ${nivel}`,
    };
    const secao = String(r.eixo ?? "").trim() || departamento || SEM_DEPARTAMENTO;
    const grupo = mapa.get(cargo) ?? { cargo, departamento, secao, opcoes: [] };
    grupo.opcoes.push(opcao);
    mapa.set(cargo, grupo);
  }
  return [...mapa.values()];
}

/**
 * Cargos das trilhas de crescimento que NÃO estão no plano (o próximo passo de
 * quem já está lá), um por nome, com o salário sugerido da trilha. O
 * departamento é o do cargo dono da trilha — é ele que diz se a pessoa é da
 * equipe de campo.
 */
export function gruposDasTrilhas(cargos: Row[], trilhas: Row[], etapas: Row[]): Grupo[] {
  const noPlano = new Set(cargos.map((c) => norm(c.cargo)));
  const vistos = new Set<string>();
  const grupos: Grupo[] = [];
  for (const e of etapas) {
    const cargo = String(e.cargo ?? "").trim();
    const chave = norm(cargo);
    if (!cargo || noPlano.has(chave) || vistos.has(chave)) continue;
    vistos.add(chave);
    const trilha = trilhas.find((t) => Number(t.id) === Number(e.trilha_id));
    const dono = trilha && cargos.find((c) => norm(c.cargo) === norm(trilha.cargo_vigente));
    const departamento = dono?.departamento ? String(dono.departamento) : null;
    grupos.push({
      cargo,
      departamento,
      secao: `Trilhas — ${String(trilha?.familia ?? "outras")}`,
      daTrilha: true,
      opcoes: [
        {
          cargo,
          departamento,
          nivel: e.nivel ? String(e.nivel) : null,
          salario: e.salario_sugerido == null || e.salario_sugerido === "" ? null : Number(e.salario_sugerido),
          texto: cargo,
          sugerido: true,
        },
      ],
    });
  }
  return grupos;
}

/**
 * Junta os cargos por DEPARTAMENTO — todos os do Operacional em sequência,
 * depois todos os do Almoxarifado, e assim por diante. Sem isso a lista sai na
 * ordem da planilha, que intercala as áreas (ex.: os cargos de campo ficam
 * lá no fim, longe de "Gestor Operacional") e fica difícil de achar.
 *
 * Os blocos (departamentos) e, dentro de cada bloco, os cargos saem em ordem
 * ALFABÉTICA (padrão do sistema — src/ordem.ts). Os NÍVEIS dentro do cargo
 * continuam na ordem de carreira que veio do servidor (Júnior → Pleno → Sênior).
 */
export function porDepartamento(grupos: Grupo[]): Secao[] {
  const mapa = new Map<string, Secao>();
  for (const g of grupos) {
    const dep = g.secao?.trim() || SEM_DEPARTAMENTO;
    const secao = mapa.get(dep) ?? { departamento: dep, daTrilha: !!g.daTrilha, grupos: [] };
    secao.grupos.push(g);
    mapa.set(dep, secao);
  }
  const secoes = ordenarPorNome([...mapa.values()], (s) => s.departamento).map((s) => ({
    ...s,
    grupos: ordenarPorNome(s.grupos, (g) => g.cargo),
  }));
  // Os cargos do plano primeiro; "Outros cargos" e os das trilhas no fim.
  return [
    ...secoes.filter((s) => s.departamento !== SEM_DEPARTAMENTO && !s.daTrilha),
    ...secoes.filter((s) => s.departamento === SEM_DEPARTAMENTO),
    ...secoes.filter((s) => s.daTrilha),
  ];
}

interface Props {
  value: string;
  /**
   * Devolve o cargo escolhido + o que a tabela sabe sobre ele (valor do nível
   * e departamento), para o formulário já sugerir esses campos.
   */
  onChange: (cargo: string, salario: number | null, departamento: string | null) => void;
  disabled?: boolean;
}

export default function CargoInput({ value, onChange, disabled }: Props) {
  const [aberto, setAberto] = useState(false);

  return (
    <>
      <div className="cargo-campo">
        <button
          type="button"
          className="cargo-botao"
          disabled={disabled}
          onClick={() => setAberto(true)}
          title="Selecionar o cargo na tabela de Cargos e Salários"
        >
          <IconBriefcase size={16} />
          <span className={value ? "cargo-valor" : "cargo-placeholder"}>
            {value || "Clique para selecionar o cargo…"}
          </span>
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="icon-btn"
            title="Limpar cargo"
            onClick={() => onChange("", null, null)}
          >
            <IconClose size={15} />
          </button>
        )}
      </div>

      {aberto && (
        <CargoPicker
          atual={value}
          onFechar={() => setAberto(false)}
          onConfirmar={(op) => {
            onChange(op.texto, op.salario, op.departamento);
            setAberto(false);
          }}
        />
      )}
    </>
  );
}

interface PickerProps {
  atual: string;
  onConfirmar: (opcao: CargoOpcao) => void;
  onFechar: () => void;
}

function CargoPicker({ atual, onConfirmar, onFechar }: PickerProps) {
  const [grupos, setGrupos] = useState<Grupo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [marcado, setMarcado] = useState<CargoOpcao | null>(null);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.list("cargos"),
      // As trilhas são complemento: se falharem, o popup segue com o plano.
      api.list("cargos_trilhas").catch(() => [] as Row[]),
      api.list("cargos_trilha_etapas").catch(() => [] as Row[]),
    ])
      .then(([rows, trilhas, etapas]) => {
        if (cancel) return;
        const gs = [...agrupar(rows), ...gruposDasTrilhas(rows, trilhas, etapas)];
        setGrupos(gs);
        // Já deixa marcado o cargo que o colaborador tem hoje.
        const atualNorm = norm(atual);
        for (const g of gs) {
          const achado = g.opcoes.find((o) => norm(o.texto) === atualNorm);
          if (achado) {
            setMarcado(achado);
            return;
          }
        }
      })
      .catch((e) => {
        if (!cancel) setErro(e instanceof Error ? e.message : "Erro ao carregar os cargos");
      });
    return () => {
      cancel = true;
    };
  }, [atual]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFechar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFechar]);

  /** Cargo que o colaborador já tinha e que não está na tabela (cadastro antigo). */
  const foraDaTabela = useMemo(() => {
    if (!atual || !grupos) return null;
    const existe = grupos.some((g) => g.opcoes.some((o) => norm(o.texto) === norm(atual)));
    return existe ? null : atual;
  }, [atual, grupos]);

  const secoes = useMemo(() => {
    if (!grupos) return [];
    const q = norm(busca);
    const filtrados = q
      ? grupos.filter(
          (g) => norm(g.cargo).includes(q) || norm(g.departamento).includes(q) || norm(g.secao).includes(q),
        )
      : grupos;
    return porDepartamento(filtrados);
  }, [grupos, busca]);

  /** Quantos cargos a busca deixou na tela (para a mensagem de "nada encontrado"). */
  const totalFiltrado = secoes.reduce((n, s) => n + s.grupos.length, 0);

  return createPortal(
    // O popup é filho do RecordForm na árvore do React (mesmo estando no body
    // pelo portal), então o clique aqui SUBIRIA até o overlay do formulário e o
    // fecharia — por isso o stopPropagation em todos os mousedown.
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        e.stopPropagation();
        onFechar();
      }}
    >
      <div className="modal-card cargo-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconBriefcase size={18} /> Selecionar cargo
          </h3>
          <button type="button" className="icon-btn" onClick={onFechar}>
            <IconClose size={18} />
          </button>
        </div>

        {/* Fora do corpo que rola: a busca fica sempre à mão na lista longa. */}
        <div className="cargo-topo">
          <div className="search-wrap cargo-busca">
            <IconSearch size={16} />
            <input
              className="search"
              placeholder="Buscar cargo, eixo ou departamento..."
              value={busca}
              autoFocus
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>

        <div className="modal-body">
          {erro && <div className="form-error">{erro}</div>}

          {!grupos && !erro && <div className="empty">Carregando cargos...</div>}

          {grupos && grupos.length === 0 && (
            <div className="empty">
              Nenhum cargo cadastrado ainda. Cadastre na aba <strong>Cargos e Salários</strong>.
            </div>
          )}

          {grupos && grupos.length > 0 && (
            <div className="cargo-lista">
              {foraDaTabela && (
                <label
                  className={`cargo-opcao cargo-opcao-atual ${
                    marcado?.texto === foraDaTabela ? "marcada" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="cargo-opcao"
                    checked={marcado?.texto === foraDaTabela}
                    onChange={() =>
                      setMarcado({
                        cargo: foraDaTabela,
                        departamento: null,
                        nivel: null,
                        salario: null,
                        texto: foraDaTabela,
                      })
                    }
                  />
                  <span className="cargo-nivel">{foraDaTabela}</span>
                  <span className="cargo-salario">manter o atual (fora da tabela)</span>
                </label>
              )}

              {totalFiltrado === 0 && (
                <div className="empty">Nenhum cargo encontrado para "{busca}".</div>
              )}

              {/* Um bloco por departamento: todos os cargos da área em sequência. */}
              {secoes.map((s) => (
                <section key={s.departamento} className="cargo-secao">
                  <div className="cargo-secao-head">
                    <span className="cargo-secao-nome">{s.departamento}</span>
                    <span className="cargo-secao-contagem">
                      {s.grupos.length} cargo{s.grupos.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  {s.grupos.map((g) => (
                    <div key={g.cargo} className="cargo-grupo">
                      <div className="cargo-grupo-head">
                        <strong>{g.cargo}</strong>
                      </div>
                      <div className="cargo-niveis">
                        {g.opcoes.map((op) => (
                          <label
                            key={op.texto}
                            className={`cargo-opcao ${marcado?.texto === op.texto ? "marcada" : ""}`}
                          >
                            <input
                              type="radio"
                              name="cargo-opcao"
                              checked={marcado?.texto === op.texto}
                              onChange={() => setMarcado(op)}
                            />
                            <span className="cargo-nivel">{op.nivel ?? "Único"}</span>
                            <span className="cargo-salario">
                              {formatCurrency(op.salario)}
                              {op.sugerido ? " sugerido" : ""}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot cargo-foot">
          <span className="cargo-marcado">
            {marcado ? (
              <>
                Selecionado: <strong>{marcado.texto}</strong>
              </>
            ) : (
              "Marque o cargo do colaborador"
            )}
          </span>
          <button type="button" className="btn-secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!marcado}
            onClick={() => marcado && onConfirmar(marcado)}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
